import { useSetRecoilState } from 'recoil';
import { Markup } from 'interweave';

import * as K from 'common/constants';
import { FETCH_ORIGIN, Settings, TRANSFORM_TYPE, TransformArgs } from 'common/types';
import { transformContent } from 'common/util/transform';
import { logToFetchState } from 'components/search/SearchLogsPage/LogResultsContainer.hooks';
import { SeeMoreState, seeMoreState } from 'components/common/Dialog/SeeMoreDialog';

type FacetValueProps = {
  field: string;
  value: string;
  filter?: string;
  settings?: Settings;
  doPrettyPrint?: boolean;
  doHighlight?: boolean;
  doLinkify?: boolean;
  disableLineBreaks?: boolean;
  isTruncated?: boolean;
  continuationToken?: string;
};

/**
 * This component displays the value of a facet. If the length of the value exceeds the provided maximum, the value
 * will be truncated and end with ... (more). Clicking on "more" shows the full value in a dialog. The value can also
 * be "linkified" if that has been set up via config, and highlighted to show text that matches the current filter.
 *
 * @prop field                 facet name
 * @prop value                 facet value
 * @prop filter                used to do highlighting of matching text within value
 * @prop settings              controls Smart Links output and escaping of returns
 * @prop doPrettyPrint         if true, pretty print JSON value
 * @prop doHighlight           if true, highlight the value based on the filter
 * @prop doLinkify             if true, linkify the value (uses settings)
 * @prop disableLineBreaks     if true, line breaks will be ignored (defaults to true)
 * @prop isTruncated           if true, value has been truncated
 * @prop continuationToken     continuation token for containing log line (used to fetch full data)
 *
 * @returns
 */
function FacetValue({
  field,
  value = '',
  filter,
  settings,
  doPrettyPrint = false,
  doHighlight = false,
  doLinkify = false,
  disableLineBreaks = true,
  isTruncated = false,
  continuationToken,
}: FacetValueProps): JSX.Element {
  const setLogToFetch = useSetRecoilState(logToFetchState);
  const setSeeMore = useSetRecoilState(seeMoreState);

  /**
   * Transform the given facet value in one or more ways, based on props:
   *
   *   1. Pretty-print (JSON)
   *   2. Add SmartLinks
   *   3. Highlight matches based on the filter
   *
   * HTML in the facet value will be escaped.
   *
   * @param facet    a facet name
   * @param text     facet value
   * @returns HTML
   */
  const transformValue = (facet: string, text: string): string => {
    const transforms: TRANSFORM_TYPE[] = [];
    const transformArgs: TransformArgs = {};
    if (doLinkify && settings) {
      transforms.push(TRANSFORM_TYPE.LINKIFY);
      transformArgs.linkConfig = settings[K.SETTING.SMART_LINKS_CONFIG];
    }
    if (doHighlight && filter) {
      transforms.push(TRANSFORM_TYPE.HIGHLIGHT);
      transformArgs.filter = filter;
    }
    if (doPrettyPrint) {
      transforms.push(TRANSFORM_TYPE.PRETTY_PRINT);
    }

    return transformContent(facet, text, transforms, transformArgs);
  };

  // set Recoil state which will result in SeeMoreDialog opening
  const showMore = (facet: string, value: string): void => {
    const html = transformValue(facet, value);
    const fullHtml = <Markup content={html} noWrap disableLineBreaks={false} />;
    const seeMore: SeeMoreState = {
      field,
      value: fullHtml,
    };
    setSeeMore(seeMore);
  };

  let adjustedValue = value.trim();
  if (field === K.FIELD.MESSAGE && settings?.[K.SETTING.UNESCAPE_LOG_LINE_MESSAGE]) {
    adjustedValue = adjustedValue.replace(/\\r/g, '\r').replace(/\\n/g, '\n');
  }

  let moreEl = null;
  const lines = adjustedValue.split(K.REGEX.LINE_RETURN);
  const getFullLogHandler = () =>
    setLogToFetch({ continuationToken: continuationToken || '', origin: FETCH_ORIGIN.VALUE, field });
  if (lines.length > K.MAX_FACET_VALUE_LINES) {
    const fullValue = adjustedValue;
    adjustedValue = lines.slice(0, K.MAX_FACET_VALUE_LINES).join('\n');
    const clickHandler = isTruncated ? getFullLogHandler : () => showMore(field, fullValue);
    moreEl = (
      <span>
        <span>&hellip;</span>
        <span data-is-more-link="true" className="link" onClick={clickHandler}>
          (more)
        </span>
      </span>
    );
  } else if (isTruncated) {
    moreEl = (
      <span>
        <span>&hellip;</span>
        <span data-is-more-link="true" className="link" onClick={getFullLogHandler}>
          (more)
        </span>
      </span>
    );
  }

  return (
    <>
      <Markup content={transformValue(field, adjustedValue)} noWrap disableLineBreaks={disableLineBreaks} />
      {moreEl}
    </>
  );
}

export default FacetValue;
